import { CreditCard } from 'types/interfaces/CreditCard';
import { Credits } from 'types/interfaces/Credits';
import { CreditsPackage } from 'types/interfaces/CreditsPackage';
import { Order } from 'types/interfaces/Order';
import { Package } from 'types/interfaces/Package';

import { PackagesThemeGroups } from './AuthApi';
import { httpClient } from './httpClient';

export interface PaymentInfo {
  payment: {
    credit_packages: CreditsPackage[];
    credits: Credits;
    packages: Package[];
    upsale_packages: Package[];
    vip_packages: Package[];
    micro_packages: Package[];
    stop_exit_package: Package[];
    lifetime_package: Package[];
    theme: PackagesThemeGroups;
    show_vip_discount: boolean;
  };
  has_limit_time_offer: boolean;
  is_additional_price: boolean;
  is_3d_split: boolean;
  is_error_translate: boolean;
  is_join_fee: boolean;
  is_credits_price_optimization: boolean;
  is_comerica_bank: boolean;
  is_one_package_introskip: number;
}

export interface PaymentFormPayload {
  packageId: string;
  upsalePackageIds: string[];
  downgradeLevel?: string | null;
  billingToken?: string;
}

export interface CreditPaymentFormPayload {
  packageId: string;
}
export interface VipPaymentFormPayload {
  packageId: string;
}
export interface PaymentFormResponse {
  url: string;
  order_id?: string;
}

export interface PaymentFormIntentResponse {
  merchant_data: {
    merchant: string;
    signature: string;
    payment_intent: string;
  };
  order_id: string;
  gpay_params?: {
    billing_token: string;
  } & Record<string, string>;
}

export interface OneClickRecurringPayload {
  cardToken: string;
  packId: string;
  isRefilled?: boolean;
}
export interface OneClickResignPayload extends OneClickRecurringPayload {
  cvv: string;
}
export interface OneClickPaymentResponse {
  credits: number | null;
  order: Order;
  verify_url: string | null;
  offer_url: string | null;
  error: string | null;
  error_code: number | null;
  is_reprocessing?: boolean;
  to_downgrade?: boolean;
}
export interface PremiumDiscountActivationResponse {
  packages: Package[];
  upsale_packages: Package[];
  credits: {
    amount: number;
    currency: string;
    gifts: Record<string, number>;
  };
}
export interface VipDiscountActivationResponse {
  packages: Package[];
}

export const PaymentApi = {
  async paymentInfo(): Promise<PaymentInfo> {
    const { data } = await httpClient.get<PaymentInfo>('/payment/payment-info');

    return data;
  },

  async fetchForm({
    packageId,
    upsalePackageIds,
    downgradeLevel,
    billingToken,
  }: PaymentFormPayload) {
    const { data } = await httpClient.post<PaymentFormResponse>(
      '/payment/premium/form',
      {
        package_info: packageId,
        extra_package_infos: upsalePackageIds,
        ...(downgradeLevel && { downgrade_level: downgradeLevel }),
        ...(billingToken && { billing_token: billingToken }),
      }
    );

    return data;
  },

  async fetchFormIntent({
    packageId,
    upsalePackageIds,
    downgradeLevel,
    billingToken,
  }: PaymentFormPayload) {
    const { data } = await httpClient.post<PaymentFormIntentResponse>(
      '/payment/premium/intent',
      {
        package_info: packageId,
        extra_package_infos: upsalePackageIds,
        ...(downgradeLevel && { downgrade_level: downgradeLevel }),
        ...(billingToken && { billing_token: billingToken }),
      }
    );

    return data;
  },

  async fetchCreditPaymentForm(payload: CreditPaymentFormPayload) {
    const { data } = await httpClient.post<PaymentFormResponse>(
      '/payment/credit/form',
      {
        package_info: payload.packageId,
      }
    );

    return data;
  },

  async fetchCreditPaymentIntentForm(payload: CreditPaymentFormPayload) {
    const { data } = await httpClient.post<PaymentFormIntentResponse>(
      '/payment/credit/intent',
      {
        package_info: payload.packageId,
      }
    );

    return data;
  },

  async fetchVipPaymentForm(payload: VipPaymentFormPayload) {
    const { data } = await httpClient.post<PaymentFormResponse>(
      '/payment/vip/form',
      {
        package_info: payload.packageId,
      }
    );

    return data;
  },

  async fetchVipPaymentIntentForm(payload: VipPaymentFormPayload) {
    const { data } = await httpClient.post<PaymentFormIntentResponse>(
      '/payment/vip/intent',
      {
        package_info: payload.packageId,
      }
    );

    return data;
  },

  async updateUpsalePackage({
    orderId,
    upsalePackageIds,
  }: {
    orderId: string;
    upsalePackageIds: string[] | null;
  }) {
    return httpClient.patch('/payment/premium/extra', {
      order: orderId,
      extra_package_infos: upsalePackageIds,
    });
  },

  fetchCreditCard() {
    return httpClient.get<{ card: CreditCard; last_decline_type: string }>(
      '/payment/card'
    );
  },

  async activatePremiumDiscount() {
    const { data } = await httpClient.post<PremiumDiscountActivationResponse>(
      '/user/discount/activate'
    );

    return data;
  },

  async activateVipDiscount() {
    const { data } = await httpClient.get<VipDiscountActivationResponse>(
      '/payment/vip/discount/activate'
    );

    return data;
  },

  async oneClickVipRecurring(payload: OneClickRecurringPayload) {
    const { data } = await httpClient.post<OneClickPaymentResponse>(
      '/payment/vip/recurring',
      {
        card: payload.cardToken,
        package_info: payload.packId,
        isRefilled: Boolean(payload.isRefilled),
      }
    );

    return data;
  },

  async oneClickVipResign(payload: OneClickResignPayload) {
    const { data } = await httpClient.post<OneClickPaymentResponse>(
      '/payment/vip/resign',
      {
        card: payload.cardToken,
        package_info: payload.packId,
        cvv: payload.cvv,
        isRefilled: Boolean(payload.isRefilled),
      }
    );

    return data;
  },

  async oneClickCreditsRecurring(payload: OneClickRecurringPayload) {
    const { data } = await httpClient.post<OneClickPaymentResponse>(
      '/payment/credit/recurring',
      {
        card: payload.cardToken,
        package_info: payload.packId,
        is_refilled: Boolean(payload.isRefilled),
      }
    );

    return data;
  },

  async oneClickCreditsResign(payload: OneClickResignPayload) {
    const { data } = await httpClient.post<OneClickPaymentResponse>(
      '/payment/credit/resign',
      {
        card: payload.cardToken,
        package_info: payload.packId,
        cvv: payload.cvv,
        is_refilled: Boolean(payload.isRefilled),
      }
    );

    return data;
  },

  microOneClickPay(payload: { packId: string; cardToken: string }) {
    return httpClient.post('/payment/micro/recurring', {
      package_info: payload.packId,
      card: payload.cardToken,
    });
  },

  microOneClickPayResign(payload: {
    packId: string;
    cardToken: string;
    cvv: string;
  }) {
    return httpClient.post('/payment/micro/resign', {
      card: payload.cardToken,
      cvv: payload.cvv,
      package_info: payload.packId,
    });
  },

  async onChangePlatinumStatus(status: boolean) {
    await httpClient.post('/platinum-status', {
      use: status,
    });
  },

  async paymentUnsubscribe(subtype: number) {
    return httpClient.get(`/payment/unsubscribe/${subtype}`);
  },
};
